import { IPromise, IQService } from 'angular';
import {
    ICartFontRight,
    ICartLicense,
    IBucket,
    IDjangoCoupon,
    IDjangoLicense,
    IDjangoPackage,
    ILicenseUpgradeDict,
    LICENSE_TYPE_DEFS,
    ZIPCODE_TAX_RATES,
    hasOwnProperty,
    ISubtotal,
    ICartLicensePrices,
    ITotals,
    IMultistyleBracket,
    ITaxRate,
} from '../tn.common';
import { LocalPackages } from '../tn.fonts/local-packages.service/localPackages';
import { hasUpgradeOrRenewCartLicenses } from '../tn.licenses/has-license-type.filter/has-upgrade-or-renew-licenses';
import { UserService } from '../tn.user/service.tn.user';
import {
    BucketService,
    getEqualOrGreaterBucketOptionValue,
} from './buckets.service/buckets';

// construct a new ICartLicenseObject with only the fontRights that match the term
// we're looking for. this is primarily used to generate the oneTime, annual, threeYears, and fiveYears
// pricing recurring charge totals.
function _filterLicenseRightsToXYears(licenses: ICartLicense[], years: number) {
    return licenses.map((license) => {
        const rights = license.fontRights;
        const _app = rights.app;
        const app = _app.years === years ? _app : { apps: 0, years: 0 };
        const _web = rights.web;
        const web = _web.years === years ? _web : { views: 0, years: 0 };
        const filtered: ICartLicense = {
            font: license.font,
            fontRights: {
                app,
                web,
                // do not include perpetual licenses
                desktop: { computers: 0 },
                epub: { ebooks: 0 },
            },
        };
        return filtered;
    });
}

/**
 * @param num The number to round
 * https://stackoverflow.com/a/18358056
 */
function roundToTwo(num) {
    return +(Math.round(num + 'e+2') + 'e-2');
}

/**
 * total is already rounded to two decimal places; Multiply by 100
 * taxRate has 5 decimal places in the worst case; Multiply by 100000
 */
function _intMultiplication(total, taxRate) {
    return (
        (Math.round(total * 100) *
            +(Math.round((1 + taxRate) * 100000 + 'e+0') + 'e-0')) /
        (100 * 100000)
    );
}

function _licenseDoesNotMatch(
    oldLicense: IDjangoLicense,
    key: string,
    qty?: number | null
) {
    return (
        !hasOwnProperty(oldLicense, key) ||
        qty === null ||
        typeof qty === 'undefined'
    );
}

function _yearsToDays(years: number): number {
    const today = new Date();
    const yearsFromToday = new Date();
    yearsFromToday.setFullYear(today.getFullYear() + years);
    return _numberOfDaysApart(yearsFromToday, today);
}

function _numberOfDaysApart(firstDate: Date, secondDate: Date): number {
    return Math.ceil((firstDate.getTime() - secondDate.getTime()) / 86400000);
}

function _isPerpetual(right: IDjangoLicense | ICartFontRight) {
    return (
        !hasOwnProperty(right, 'years') ||
        // the first two checks are for ICartFontRight, which doesn't have a years property on always perpetual licenses
        // and takes unlimited as an option for termable licenses.
        right.years === 'unlimited' ||
        right.years === null ||
        // this is check for IDjangoLicense, where years is always set to a numeric value.
        right.years === -1
    );
}

function _calculateProratedPrice(
    right: ICartFontRight,
    oldRight: IDjangoLicense,
    price: number,
    newPerpetual
) {
    // Otherwise we handle prorating
    const oldLicensePrice = parseFloat(oldRight.price_paid) || 0;
    const freeTrial =
        hasOwnProperty(oldRight, 'years') && oldRight.years === -2;

    // If original license is perpetual we do a full discount since we can't prorate it
    if (freeTrial || _isPerpetual(oldRight)) {
        return price - oldLicensePrice;
    }

    let oldRemainingDays = _numberOfDaysApart(
        new Date(oldRight.end || 0),
        new Date()
    );
    const oldTotalDays = _numberOfDaysApart(
        new Date(oldRight.end || 0),
        new Date(oldRight.start)
    );
    let oldPercentUnused; // Decimal
    let newPercentUnused; // Decimal
    if (oldRemainingDays > 0) {
        oldPercentUnused = oldRemainingDays / oldTotalDays;
    } else {
        oldPercentUnused = 0;
        oldRemainingDays = 0;
    }
    // If this is an upgrade, but not a renewal
    if (!hasOwnProperty(right, 'renew') || right.renew === false) {
        // There's a corner case here where if you renew a free license for a year, then
        // upgrade the resulting license we don't count the extra 30 days when trying to upgrade it.
        newPercentUnused = oldRemainingDays / _yearsToDays(oldRight.years);
    } else {
        newPercentUnused =
            (_yearsToDays(right.years) + oldRemainingDays) /
            _yearsToDays(right.years);
    }

    if (newPerpetual) {
        return price - oldLicensePrice * oldPercentUnused;
    } else {
        return price * newPercentUnused - oldLicensePrice * oldPercentUnused;
    }
}

function _getBasePrice(buckets: IBucket, license: ICartLicense) {
    const packageBasePrice = license.packageBasePrice;
    if (packageBasePrice) {
        return parseFloat(packageBasePrice.toString());
    }
    if (license.font.base_price) {
        return parseFloat(license.font.base_price);
    }
    return parseFloat(buckets.basePrice.toString());
}

function _getTaxRate(state: string, zipcode: string) {
    // We're only concerned with taxes from NY, NYC, and MA for now
    const normalizedZipcode = parseInt(zipcode.slice(0, 5));
    // MD, RI, NJ and MA states have one sales tax for the entire state
    switch (state) {
        case 'MA':
            return 6.25 / 100;
        // case 'NJ':
        //     return 6.625 / 100;
        case 'MD':
            return 6 / 100;
        case 'NY':
        case 'WA':
        case 'FL':
        case 'VA':
        case 'OH':
        case 'NC':
        case 'TX':
            return ZIPCODE_TAX_RATES[normalizedZipcode] / 100;
        default:
            return 0;
    }
}

function sortPackagesByNumStylesThenName(a: IDjangoPackage, b: IDjangoPackage) {
    if (a.styles.length < b.styles.length) {
        return 1;
    }
    if (b.styles.length < a.styles.length) {
        return -1;
    }
    if (a.name < b.name) {
        return 1;
    }
    if (b.name < a.name) {
        return -1;
    }
    return 0;
}

export class Pricing {
    discountedFamilyStyles = {};
    _taxRate: ITaxRate = { rate: null, state: null };
    multiStyleBrackets: IMultistyleBracket[] = [
        { min: 1, max: 1, discount: 0 },
        { min: 2, max: 4, discount: 10 },
        { min: 5, max: 8, discount: 12.5 },
        { min: 9, max: 16, discount: 15 },
        { min: 17, discount: 17.5 }, // 1. missing 'max' means min+ 2. Edit also in backend
    ];

    static $inject = ['$q', 'tnBuckets', 'localPackages', 'tn.user.service'];
    constructor(
        protected $q: IQService,
        protected bucketsService: BucketService,
        protected localPackages: LocalPackages,
        protected userService: UserService
    ) {}

    _matchPackagePrices(
        licenses: ICartLicense[],
        sortedPackages: IDjangoPackage[]
    ): void {
        const ids: number[] = [];
        function rebuildRemainigStyles() {
            ids.length = 0; // fast clear, no allocation
            licenses.forEach((l) => {
                if (
                    !hasOwnProperty(l, 'packageBasePrice') &&
                    ids.indexOf(l.font.id) === -1
                ) {
                    ids.push(l.font.id);
                }
            });
        }

        sortedPackages.forEach((pack) => {
            rebuildRemainigStyles();

            const overlap =
                pack.styles.filter((id) => ids.indexOf(id) > -1).length ===
                pack.styles.length;

            if (overlap) {
                const updatePrice = parseFloat(pack.price) / pack.styles.length;
                licenses
                    .filter((l) => pack.styles.indexOf(l.font.id) > -1)
                    .forEach((l) => (l.packageBasePrice = updatePrice));
            }
        });
    }

    calculateLicensePrices(
        licenses: ICartLicense[]
    ): IPromise<ICartLicensePrices[]> {
        const clonedLicenses = licenses;
        return this.__getSortedPackages()
            .then((sortedPackages) => {
                this._matchPackagePrices(clonedLicenses, sortedPackages);
                this.discountedFamilyStyles =
                    this.getDiscountedFamilyStyles(clonedLicenses);
                return this.$q.all(
                    clonedLicenses.map((license) =>
                        this.calculateLicensePrice(license)
                    )
                );
            })
            .then((result) => {
                // remove internal tracking data here!!
                licenses.forEach((l) => delete l.packageBasePrice);
                return result;
            });
    }

    calculateLicensePrice(license: ICartLicense): IPromise<ICartLicensePrices> {
        return this.bucketsService.getBuckets(license.font).then((buckets) => {
            const discount = this.getDiscount(license, buckets);
            const cpuPrice = this.calculateCpuPrice(buckets, license, discount);
            const appPrice = this.calculateAppPrice(buckets, license, discount);
            const epubPrice = this.calculateEpubPrice(
                buckets,
                license,
                discount
            );
            const webPrice = this.calculateWebPrice(buckets, license, discount);
            const base =
                cpuPrice.base + appPrice.base + epubPrice.base + webPrice.base;
            const discounted =
                cpuPrice.discounted +
                appPrice.discounted +
                epubPrice.discounted +
                webPrice.discounted;
            const prices: ICartLicensePrices = {
                fontId: license.font.id,
                base: base,
                discounted: discounted,
                appPrice: appPrice,
                desktopPrice: cpuPrice,
                epubPrice: epubPrice,
                webPrice: webPrice,
                adjustedTotal: discounted,
                priceAdjustment: 0,
            };
            return prices;
        });
    }

    calculateTotals(
        licenses: ICartLicense[],
        coupon?: IDjangoCoupon,
        licensesUpgradeDict?: ILicenseUpgradeDict
    ): IPromise<ITotals> {
        // To do:
        //   If coupon is restricted by family or foundry the discount has to be applied
        //   to the fonts that meet the restriction criteria only. Do not apply coupon to the whole cart
        const oneTime = licenses.map((license) => {
            const rights = license.fontRights;
            const result: ICartLicense = {
                font: license.font,
                fontRights: {
                    app:
                        rights.app.years === 'unlimited'
                            ? rights.app
                            : { apps: 0, years: 0 },
                    desktop: rights.desktop.computers
                        ? rights.desktop
                        : { computers: 0 },
                    epub: rights.epub.ebooks ? rights.epub : { ebooks: 0 },
                    web:
                        rights.web.years === 'unlimited'
                            ? rights.web
                            : { views: 0, years: 0 },
                },
            };
            return result;
        });
        const subtotal = hasUpgradeOrRenewCartLicenses(licenses)
            ? this.calculateSubTotal(licenses, licensesUpgradeDict)
            : this.calculateSubTotal(licenses);

        return this.$q
            .all({
                oneTime: this.calculateSubTotal(oneTime),
                annual: this.calculateSubTotal(
                    _filterLicenseRightsToXYears(licenses, 1)
                ),
                threeYears: this.calculateSubTotal(
                    _filterLicenseRightsToXYears(licenses, 3)
                ),
                fiveYears: this.calculateSubTotal(
                    _filterLicenseRightsToXYears(licenses, 5)
                ),
                subtotal: subtotal,
            })
            .then(({ oneTime, annual, threeYears, fiveYears, subtotal }) => {
                let couponDiscount = 0;
                if (coupon) {
                    const discount = coupon.discount;
                    if (coupon.discount_type === 0) {
                        // don't go below 0
                        couponDiscount = Math.min(
                            subtotal.discounted,
                            discount
                        );
                    } else if (coupon.discount_type === 1) {
                        couponDiscount = roundToTwo(
                            subtotal.discounted * (discount / 100.0)
                        );
                    }
                }

                let bulkDiscount = roundToTwo(
                    subtotal.base - subtotal.discounted
                );
                let withoutTax = roundToTwo(
                    subtotal.base - bulkDiscount - couponDiscount
                );
                let priceAdjustment = 0;
                if (subtotal.priceAdjustment) {
                    if (coupon && coupon.discount_type === 1) {
                        couponDiscount =
                            subtotal.adjustedTotal * (coupon.discount / 100.0);
                    }
                    if (couponDiscount >= subtotal.adjustedTotal) {
                        couponDiscount = subtotal.adjustedTotal;
                    }
                    bulkDiscount = roundToTwo(
                        subtotal.base - subtotal.adjustedTotal
                    );
                    priceAdjustment = subtotal.priceAdjustment;
                    withoutTax = roundToTwo(
                        subtotal.base - bulkDiscount - couponDiscount
                    );
                }
                // Ensure we don't below zero
                withoutTax = Math.max(withoutTax, 0);
                // Round up to two decimal places, to match whats going on server side
                const notRoundedTotal = _intMultiplication(
                    withoutTax,
                    this._taxRate.rate
                );
                const withTax = roundToTwo(notRoundedTotal);
                const result: ITotals = {
                    coupon: couponDiscount,
                    oneTime,
                    annual,
                    threeYears,
                    fiveYears,
                    subtotal,
                    finals: {
                        withoutTax,
                        withTax,
                        priceAdjustment,
                    },
                };
                return result;
            });
    }

    calculateSubTotal(
        licenses: ICartLicense[],
        licensesUpgradeDict?: ILicenseUpgradeDict
    ): IPromise<ISubtotal> {
        const initial = {
            base: 0,
            discounted: 0,
            priceAdjustment: 0,
            adjustedTotal: 0,
        };
        if (licenses.length === 0) {
            return this.$q.when(initial);
        }

        return this.calculateLicensePrices(licenses)
            .then((prices) => {
                return {
                    total: prices.reduce<ISubtotal>((acc, licensePrice) => {
                        acc.base += licensePrice.base;
                        acc.discounted += licensePrice.discounted;
                        acc.adjustedTotal += licensePrice.discounted;
                        return acc;
                    }, initial),
                    prices: prices,
                };
            })
            .then((res) => {
                // If we might be upgrading or renewing licenses then calculate the price adjustment
                if (licensesUpgradeDict) {
                    const adjustments = this._totalUpgradePriceAdjustment(
                        licenses,
                        licensesUpgradeDict,
                        res.prices
                    );
                    res.total.priceAdjustment = adjustments.priceAdjustment;
                    res.total.adjustedTotal =
                        res.total.discounted - adjustments.priceAdjustment;
                }
                return res.total;
            });
    }

    _totalUpgradePriceAdjustment(
        licenses: ICartLicense[],
        licensesUpgradeDict: ILicenseUpgradeDict,
        prices: ICartLicensePrices[]
    ): { priceAdjustment: number } {
        if (Object.keys(licensesUpgradeDict).length === 0) {
            return { priceAdjustment: 0 };
        }

        let priceAdjustment = 0;
        const totals = {};

        licenses.forEach((license) => {
            const oldLicense = hasOwnProperty(
                licensesUpgradeDict,
                license.font.id.toString()
            )
                ? licensesUpgradeDict[license.font.id].fontRights
                : false;

            const fontFamilyId = license.font.family;

            if (!totals[fontFamilyId]) {
                totals[fontFamilyId] = {
                    familyTotal: 0,
                    familyAdjustedTotal: 0,
                };
            }

            Object.keys(license.fontRights).forEach((key) => {
                // If there's no match then we're looking at the wrong font right
                const qty = license.fontRights[key][LICENSE_TYPE_DEFS[key].key];
                if (_licenseDoesNotMatch(oldLicense, key, qty)) {
                    return;
                }
                const oldRight = oldLicense[key];
                const right = license.fontRights[key];
                const price = prices.filter(
                    (priceObj) => priceObj.fontId === license.font.id
                )[0][key + 'Price'].discounted;
                const newPerpetual = _isPerpetual(right);

                // If we're renewing without upgrading, but we're not switching to perpetual
                if (
                    !newPerpetual &&
                    hasOwnProperty(right, 'renew') &&
                    right.renew === true &&
                    (!hasOwnProperty(right, 'upgrade') ||
                        right.upgrade === false)
                ) {
                    totals[fontFamilyId]['familyTotal'] += price;
                    totals[fontFamilyId]['familyAdjustedTotal'] += price;
                } else if (
                    (hasOwnProperty(right, 'upgrade') &&
                        right.upgrade === true) ||
                    newPerpetual
                ) {
                    totals[fontFamilyId]['familyTotal'] += price;
                    const proratedPrice = _calculateProratedPrice(
                        right,
                        oldRight,
                        price,
                        newPerpetual
                    );
                    // Never refund more than the cost of the new license
                    if (proratedPrice > 0) {
                        totals[fontFamilyId]['familyAdjustedTotal'] +=
                            proratedPrice;
                    }
                }
            });
        });

        // Round priceAdjustment by family to avoid rounding errors
        Object.keys(totals).forEach((key) => {
            priceAdjustment += Math.round(
                totals[key]['familyTotal'] - totals[key]['familyAdjustedTotal']
            );
        });

        // Round adjusted total to nearest dollar
        return {
            priceAdjustment: priceAdjustment,
        };
    }

    calculateAppPrice(
        buckets: IBucket,
        license: ICartLicense,
        discount: number
    ): ISubtotal {
        if (!buckets.app || !license.fontRights.app) {
            return {
                base: 0,
                discounted: 0,
                adjustedTotal: 0,
                priceAdjustment: 0,
            };
        }
        const base =
            _getBasePrice(buckets, license) *
            buckets.platform.app *
            getEqualOrGreaterBucketOptionValue(
                buckets.app,
                license.fontRights.app.apps || 0
            ) *
            getEqualOrGreaterBucketOptionValue(
                buckets.year,
                license.fontRights.app.years || 0
            );
        const discounted = base - base * discount;
        return {
            base: base,
            discounted: base ? discounted : 0,
            adjustedTotal: base ? discounted : 0,
            priceAdjustment: 0,
        };
    }

    calculateCpuPrice(
        buckets: IBucket,
        license: ICartLicense,
        discount: number
    ): ISubtotal {
        if (!buckets.cpu || !license.fontRights.desktop) {
            return {
                base: 0,
                discounted: 0,
                adjustedTotal: 0,
                priceAdjustment: 0,
            };
        }
        const base =
            _getBasePrice(buckets, license) *
            buckets.platform.cpu *
            getEqualOrGreaterBucketOptionValue(
                buckets.cpu,
                license.fontRights.desktop.computers || 0
            );
        const discounted = base - base * discount;
        return {
            base: base,
            discounted: base ? discounted : 0,
            adjustedTotal: base ? discounted : 0,
            priceAdjustment: 0,
        };
    }

    calculateEpubPrice(
        buckets: IBucket,
        license: ICartLicense,
        discount: number
    ): ISubtotal {
        if (!buckets.ebook || !license.fontRights.epub) {
            return {
                base: 0,
                discounted: 0,
                adjustedTotal: 0,
                priceAdjustment: 0,
            };
        }
        const base =
            _getBasePrice(buckets, license) *
            buckets.platform.ebook *
            getEqualOrGreaterBucketOptionValue(
                buckets.ebook,
                license.fontRights.epub.ebooks || 0
            );
        const discounted = base - base * discount;
        return {
            base: base,
            discounted: base ? discounted : 0,
            adjustedTotal: base ? discounted : 0,
            priceAdjustment: 0,
        };
    }

    calculateWebPrice(
        buckets: IBucket,
        license: ICartLicense,
        discount: number
    ): ISubtotal {
        if (!buckets.web || !license.fontRights.web) {
            return {
                base: 0,
                discounted: 0,
                adjustedTotal: 0,
                priceAdjustment: 0,
            };
        }
        let base =
            _getBasePrice(buckets, license) *
            buckets.platform.web *
            getEqualOrGreaterBucketOptionValue(
                buckets.year,
                license.fontRights.web.years || 0
            );
        if (license.fontRights.web.self_hosted) {
            base =
                base *
                getEqualOrGreaterBucketOptionValue(
                    buckets.web_self_hosted,
                    license.fontRights.web.views || 0
                );
        } else {
            base =
                base *
                getEqualOrGreaterBucketOptionValue(
                    buckets.web,
                    license.fontRights.web.views || 0
                );
        }
        const discounted = base - base * discount;

        return {
            base: base,
            discounted: base ? discounted : 0,
            adjustedTotal: base ? discounted : 0,
            priceAdjustment: 0,
        };
    }

    getTaxRate(): ITaxRate {
        return this._taxRate;
    }

    // set value for this._taxRate, this._taxRate should be a local var not a property. If we had multiple
    // pricing calculations happening asynchronously storing it on the class could create cases where the wrong
    // tax rate is used.
    calculateTax(country: string, state: string, zipcode: string): void {
        if (country.toLowerCase() !== 'us') {
            this._taxRate['rate'] = 0;
            this._taxRate['state'] = null;
            return;
        }

        this._taxRate['state'] = state;
        const user = this.userService.getUser();
        if (user?.tax_exempt) {
            this._taxRate['rate'] = 0;
        } else {
            this._taxRate['rate'] = _getTaxRate(state, zipcode);
        }
    }

    clearTaxRate(): void {
        this._taxRate.rate = null;
        this._taxRate.state = null;
    }

    _getMultiStyleBrackets(buckets: IBucket): IMultistyleBracket[] {
        if (buckets && !buckets.default) {
            const multiStyles = Object.entries(buckets['style']);
            const result: IMultistyleBracket[] = [];
            for (const [i, [key, value]] of Object.entries(multiStyles)) {
                const idx = Number.parseInt(i);
                const data = { min: Number.parseInt(key), discount: value };
                if (multiStyles.length !== idx + 1) {
                    const nexKey = Number.parseInt(multiStyles[idx + 1][0]);
                    data['max'] = nexKey - 1;
                }
                result.push(data);
            }
            return result;
        }
        return this.multiStyleBrackets;
    }

    getDiscount(license: ICartLicense, buckets: IBucket): number {
        // if the license has been marked as a part of a package, we do not apply the
        // style discount since it is already intrinsic to the package.
        if (hasOwnProperty(license, 'packageBasePrice')) {
            return 0;
        }

        let discount = 0;
        const family = license.font.family;
        const styleBrackets = this._getMultiStyleBrackets(buckets);
        styleBrackets.forEach((bracket) => {
            if (this.discountedFamilyStyles[family] >= bracket.min) {
                const max = bracket.max;
                if (!max || this.discountedFamilyStyles[family] <= max) {
                    discount = bracket.discount / 100;
                    return;
                }
            }
        });
        return discount;
    }

    getDiscountedFamilyStyles(
        licenses: ICartLicense[]
    ): Record<number, number> {
        const discountedFamilyStyles: Record<number, number> = {};
        licenses.forEach(function (license) {
            // skip if we've manually marked this as being part of a package
            if (hasOwnProperty(license, 'packageBasePrice')) {
                return;
            }

            const family = license.font.family;
            if (hasOwnProperty(discountedFamilyStyles, family.toString())) {
                discountedFamilyStyles[family]++;
            } else {
                discountedFamilyStyles[family] = 1;
            }
        });
        return discountedFamilyStyles;
    }

    // caches sort result
    __getSortedPackages(): IPromise<IDjangoPackage[]> {
        return this.localPackages.getPackages().then((packages) => {
            return [...packages].sort(sortPackagesByNumStylesThenName);
        });
    }
}
